import { endOfDay, format, parseISO, startOfDay, startOfHour } from 'date-fns';
import { de, enUS, es, fr, pl } from 'date-fns/locale';

/**
 * Format date to "Month Day, Time" format (e.g., "September 15, 18:00")
 * @param dateString - ISO 8601 string (UTC)
 * @param language - Language code for locale
 * @param timezone - Optional IANA timezone string. If provided, formats the UTC date in this timezone instead of browser timezone
 */
export const formatDateMonthDayTime = (dateString: string, language: string = 'en', timezone?: string | null): string => {
  if (!dateString) return '';

  try {
    const date = parseISO(dateString);

    // If timezone is provided, format in that timezone instead of browser timezone
    if (timezone) {
      // Map language codes to Intl locale codes
      const localeMap: Record<string, string> = {
        en: 'en-US',
        es: 'es-ES',
        fr: 'fr-FR',
        de: 'de-DE',
        pl: 'pl-PL'
      };

      const intlLocale = localeMap[language] || 'en-US';

      const formatter = new Intl.DateTimeFormat(intlLocale, {
        timeZone: timezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // Use formatToParts to get individual components and format them manually
      // This ensures consistent format "Month Day, Time" regardless of locale defaults
      const parts = formatter.formatToParts(date);
      const month = parts.find((p) => p.type === 'month')?.value || '';
      const day = parts.find((p) => p.type === 'day')?.value || '';
      const hour = parts.find((p) => p.type === 'hour')?.value || '';
      const minute = parts.find((p) => p.type === 'minute')?.value || '';

      return `${month} ${day}, ${hour}:${minute}`;
    }

    return format(date, 'MMMM d, HH:mm', { locale: getLocale(language) });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format date to "Month Day at Time" format (e.g., "July 22 at 19:00")
 * @param dateString - ISO 8601 string (UTC)
 * @param language - Language code for locale
 * @param timezone - Optional IANA timezone string. If provided, formats the UTC date in this timezone instead of browser timezone
 */
export const formatDateMonthDayAtTime = (dateString: string, language: string = 'en', timezone?: string | null): string => {
  if (!dateString) return '';

  try {
    const date = parseISO(dateString);

    // If timezone is provided, format in that timezone instead of browser timezone
    if (timezone) {
      // Map language codes to Intl locale codes
      const localeMap: Record<string, string> = {
        en: 'en-US',
        es: 'es-ES',
        fr: 'fr-FR',
        de: 'de-DE',
        pl: 'pl-PL'
      };

      const intlLocale = localeMap[language] || 'en-US';

      const formatter = new Intl.DateTimeFormat(intlLocale, {
        timeZone: timezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // Use formatToParts to get individual components and format them manually
      // This ensures consistent format "Month Day at Time" regardless of locale defaults
      const parts = formatter.formatToParts(date);
      const month = parts.find((p) => p.type === 'month')?.value || '';
      const day = parts.find((p) => p.type === 'day')?.value || '';
      const hour = parts.find((p) => p.type === 'hour')?.value || '';
      const minute = parts.find((p) => p.type === 'minute')?.value || '';

      return `${month} ${day} at ${hour}:${minute}`;
    }

    return format(date, "MMMM d 'at' HH:mm", { locale: getLocale(language) });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Get the appropriate locale for date-fns based on language code
 */
const getLocale = (language: string) => {
  switch (language) {
    case 'es':
      return es;
    case 'fr':
      return fr;
    case 'de':
      return de;
    case 'pl':
      return pl;
    default:
      return enUS;
  }
};

export const formatDateByLanguage = (date: string | Date, formatString: string, language: string = 'es'): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;

    return format(dateObj, formatString, { locale: getLocale(language) });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format work history date range as MM/YYYY - MM/YYYY
 * @param startedAt - Start date string
 * @param endedAt - End date string (optional)
 * @returns Formatted date range string
 */
export const formatWorkHistoryDateRange = (startedAt: string, endedAt?: string): string => {
  try {
    const startDate = startedAt ? parseISO(startedAt) : null;
    const endDate = endedAt ? parseISO(endedAt) : null;

    if (!startDate) return '';

    const startFormatted = format(startDate, 'MM/yyyy');

    if (!endDate) {
      return `${startFormatted} - Present`;
    }

    const endFormatted = format(endDate, 'MM/yyyy');
    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    console.error('Error formatting work history date range:', error);
    return '';
  }
};

/**
 * Get API datetime for reservations (simplified - no timezone conversion)
 * @param type - 'since' or 'until'
 * @param useStartOfHour - Whether to use start of hour instead of start/end of day
 * @returns ISO string in UTC format
 */
export const getApiDateTimeSimple = (type: 'since' | 'until', useStartOfHour = false): string => {
  const now = new Date();

  let targetTime: Date;
  if (useStartOfHour) {
    // Use start of current hour
    targetTime = startOfHour(now);
  } else {
    // Use start/end of day
    targetTime = type === 'since' ? startOfDay(now) : endOfDay(now);
  }

  return targetTime.toISOString();
};

/**
 * Format a Date to a local day key yyyy-MM-dd (avoids UTC drift vs toISOString)
 */
export const formatLocalDateKey = (date: Date): string => format(date, 'yyyy-MM-dd');

/**
 * Extract date key (yyyy-MM-dd) from UTC ISO string without timezone conversion
 * This ensures dates from backend (UTC) are displayed correctly in calendar
 */
export const formatUtcDateKey = (isoString: string): string => {
  if (!isoString) return '';
  // Extract date part directly from ISO string (YYYY-MM-DDTHH:mm:ss...)
  // This avoids timezone conversion issues
  return isoString.split('T')[0];
};

/**
 * Extract date key (yyyy-MM-dd) from UTC ISO string converted to restaurant timezone
 * @param isoString - UTC ISO string (e.g., "2024-01-15T10:00:00Z")
 * @param timezone - IANA timezone string (e.g., "Europe/Madrid"), null for UTC, or undefined if restaurant not loaded yet
 * @returns Date key in format yyyy-MM-dd in restaurant timezone, or empty string if timezone is undefined
 */
export const formatDateKeyWithTimezone = (isoString: string, timezone: string | null | undefined = null): string => {
  if (!isoString) return '';

  // If timezone is undefined, restaurant is still loading - return empty string to prevent incorrect grouping
  if (timezone === undefined) {
    return '';
  }

  if (!timezone || timezone === 'UTC') {
    return formatUtcDateKey(isoString);
  }

  try {
    const utcDate = parseISO(isoString);

    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    return formatter.format(utcDate);
  } catch (error) {
    console.warn('Invalid timezone, using UTC:', error);
    return formatUtcDateKey(isoString);
  }
};

/**
 * Format time (HH:mm) from UTC ISO string in restaurant timezone
 * @param isoString - UTC ISO string (e.g., "2024-01-15T10:00:00Z")
 * @param timezone - IANA timezone string (e.g., "Europe/Madrid"), null for UTC, or undefined to use browser local
 */
export const formatTimeWithTimezone = (isoString: string, timezone: string | null | undefined = undefined): string => {
  if (!isoString) return '';

  try {
    const date = parseISO(isoString);

    // If timezone is undefined, fall back to browser local time
    if (timezone === undefined) {
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }

    const timeString = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone || 'UTC'
    });

    // Check if browser locale settings might have messed up the 24h format or AM/PM
    // This is a safety check because some browsers might ignore hour12: false with certain locales
    if (timeString.match(/am|pm/i)) {
      // Fallback to manual formatting if needed, but Intl should handle it
      const formatter = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone || 'UTC'
      });
      return formatter.format(date);
    }

    return timeString;
  } catch (error) {
    console.error('Error formatting time with timezone:', error);
    return '';
  }
};

/**
 * Normalize ISO date string to ensure it has Z suffix (UTC indicator)
 * FullCalendar requires explicit UTC indicator to properly handle timeZone conversion
 * @param isoString - ISO date string (may or may not have Z suffix)
 * @returns ISO string with Z suffix if it was missing
 */
export const normalizeToUtcIso = (isoString: string): string => {
  if (!isoString) return isoString;

  // If already has Z or timezone offset, return as is
  if (isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString)) {
    return isoString;
  }

  // If ends with just time (HH:mm:ss), add Z
  if (/T\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(isoString)) {
    return isoString + 'Z';
  }

  return isoString;
};

/**
 * Convert UTC ISO string to local timezone ISO string for FullCalendar
 * This converts a UTC date to the restaurant timezone and returns it as a local ISO string
 * (without timezone indicator, so FullCalendar treats it as local time)
 * @param utcIsoString - UTC ISO string (e.g., "2024-01-15T16:00:00Z")
 * @param timezone - IANA timezone string (e.g., "Europe/Madrid") or null for UTC
 * @returns ISO string in local format (YYYY-MM-DDTHH:mm:ss) representing the time in restaurant timezone
 */
export const convertUtcToLocalIsoForCalendar = (utcIsoString: string, timezone: string | null = null): string => {
  if (!utcIsoString) return utcIsoString;

  try {
    const utcDate = parseISO(utcIsoString);

    if (!timezone || timezone === 'UTC') {
      // If no timezone, return as local ISO (without Z)
      const year = utcDate.getUTCFullYear();
      const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getUTCDate()).padStart(2, '0');
      const hours = String(utcDate.getUTCHours()).padStart(2, '0');
      const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
      const seconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }

    // Convert UTC to restaurant timezone and format as local ISO
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(utcDate);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    const hour = parts.find((p) => p.type === 'hour')?.value || '';
    const minute = parts.find((p) => p.type === 'minute')?.value || '';
    const second = parts.find((p) => p.type === 'second')?.value || '00';

    return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  } catch (error) {
    console.warn('Error converting UTC to local ISO for calendar:', error);
    return utcIsoString;
  }
};

/**
 * Convert date and time string to ISO 8601 format in UTC
 * The input time is interpreted as local time in the restaurant timezone
 * @param dateTime - String in format "YYYY-MM-DD HH:MM" (time in restaurant timezone)
 * @param timezone - IANA timezone string (e.g., "Europe/Madrid") or null for UTC
 * @returns ISO 8601 string in UTC format
 */
export const formatDateTimeWithTimezone = (dateTime: string, timezone: string | null = null): string => {
  if (!dateTime) return '';

  const [datePart, timePart] = dateTime.split(' ');
  if (!datePart || !timePart) return dateTime;

  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  if (!timezone || timezone === 'UTC') {
    const date = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
    return date.toISOString();
  }

  try {
    // The input time is in restaurant timezone, we need to convert it to UTC
    // Strategy: use Intl API to find the UTC time that corresponds to this local time

    const yearStr = String(year);
    const monthStr = String(month).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const hourStr = String(hours).padStart(2, '0');
    const minuteStr = String(minutes).padStart(2, '0');
    const dateStr = `${yearStr}-${monthStr}-${dayStr}T${hourStr}:${minuteStr}:00`;

    // Create a formatter for the restaurant timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // We need to find the UTC time that, when formatted in restaurant timezone, gives us the input time
    // Start with a reasonable guess: treat input as UTC and adjust based on offset

    // Create a candidate UTC date (start with treating input as UTC)
    let candidateUtc = new Date(`${dateStr}Z`);

    // Iteratively adjust until we find the correct UTC time
    for (let iteration = 0; iteration < 10; iteration++) {
      const parts = formatter.formatToParts(candidateUtc);
      const tzYear = parseInt(parts.find((p) => p.type === 'year')?.value || '0', 10);
      const tzMonth = parseInt(parts.find((p) => p.type === 'month')?.value || '0', 10);
      const tzDay = parseInt(parts.find((p) => p.type === 'day')?.value || '0', 10);
      const tzHour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
      const tzMinute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);

      // Check if we've found the exact match
      if (tzYear === year && tzMonth === month && tzDay === day && tzHour === hours && tzMinute === minutes) {
        return candidateUtc.toISOString();
      }

      // Calculate the difference between desired time and what candidateUtc represents in restaurant timezone
      // We need to adjust candidateUtc so that when formatted in restaurant timezone, it matches the input

      // Calculate difference in minutes
      const desiredTotalMinutes = hours * 60 + minutes;
      const tzTotalMinutes = tzHour * 60 + tzMinute;
      const timeDiffMinutes = desiredTotalMinutes - tzTotalMinutes;

      // Calculate date difference in days, then convert to minutes
      // Use a simple approach: calculate days difference
      const desiredDate = new Date(year, month - 1, day);
      const tzDate = new Date(tzYear, tzMonth - 1, tzDay);
      const dateDiffDays = Math.round((desiredDate.getTime() - tzDate.getTime()) / (1000 * 60 * 60 * 24));
      const totalDiffMinutes = timeDiffMinutes + dateDiffDays * 24 * 60;

      // Adjust the candidate UTC time
      candidateUtc = new Date(candidateUtc.getTime() + totalDiffMinutes * 60 * 1000);
    }

    // Return the best approximation we found
    return candidateUtc.toISOString();
  } catch (error) {
    console.warn('Invalid timezone, using UTC:', error);
    const date = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
    return date.toISOString();
  }
};
